/**
 * WhiteLabelContext
 *
 * Resolves the current tenant's brand configuration based on:
 * 1. Custom domain → lookup programCode via API
 * 2. Subdomain pattern → extract programCode
 * 3. URL path /p/CODE → extract programCode
 * 4. Fallback → default EPPREM branding
 *
 * Uses localStorage caching for instant no-flicker load.
 * Brand is resolved once on mount and can be refreshed.
 */
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { programUISettingsService } from '@/modules/programs/services/programUISettingsService';
import {
  type BrandConfig,
  DEFAULT_BRAND,
  resolveTenantFromHostname,
  getCachedBrand,
  setCachedBrand,
  applyBrandToDocument,
  sanitizeBrandConfig,
} from '@/modules/shared/utils/whiteLabelResolver';

// ─────────────────────────────────────────────────────────────────────────────
// Context Type
// ─────────────────────────────────────────────────────────────────────────────

interface WhiteLabelContextType {
  brand: BrandConfig;
  isLoading: boolean;
  isWhiteLabeled: boolean;
  tenantType: 'subdomain' | 'custom-domain' | 'none';
  refreshBrand: () => Promise<void>;
}

const WhiteLabelCtx = createContext<WhiteLabelContextType | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export function WhiteLabelProvider({ children }: { children: ReactNode }) {
  const tenant = resolveTenantFromHostname();

  // Try to load from cache immediately (no flicker)
  const cacheKey = tenant.value || 'default';
  const [brand, setBrand] = useState<BrandConfig>(() => {
    return getCachedBrand(cacheKey) || DEFAULT_BRAND;
  });
  const [isLoading, setIsLoading] = useState(tenant.type !== 'none');

  const fetchBrand = useCallback(async () => {
    if (tenant.type === 'none') {
      setBrand(DEFAULT_BRAND);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      let programCode: string | null = null;

      if (tenant.type === 'subdomain') {
        programCode = tenant.value;
      } else if (tenant.type === 'custom-domain') {
        // For custom domains, we need to resolve via API
        // Use the URL-based public endpoint
        const response = await programUISettingsService.getPublicSettingsByUrl(tenant.value!);
        if (response.success && response.data) {
          const resolved = sanitizeBrandConfig({
            programId: null,
            programCode: response.data.programName || null,
            name: response.data.tabTitle || response.data.programName || DEFAULT_BRAND.name,
            logo: response.data.loginPageLogoUrl || '',
            favicon: response.data.faviconUrl || '',
            sidebarLogo: response.data.internalSidebarLogoUrl || '',
            theme: {
              primaryColor: response.data.primaryColor || DEFAULT_BRAND.theme.primaryColor,
              secondaryColor: response.data.secondaryColor || DEFAULT_BRAND.theme.secondaryColor,
              accentColor: response.data.accentColor || DEFAULT_BRAND.theme.accentColor,
            },
            domain: tenant.value,
            loginBackground: {
              type: response.data.loginBackgroundType || 'gradient',
              value: response.data.loginBackgroundValue || '',
              patternEnabled: response.data.loginBackgroundPatternEnabled ?? true,
            },
            loginPageTitle: response.data.loginPageTitle || DEFAULT_BRAND.loginPageTitle,
            loginPageSubtitle: response.data.loginPageSubtitle || DEFAULT_BRAND.loginPageSubtitle,
            loginPageDescription: response.data.loginPageDescription || DEFAULT_BRAND.loginPageDescription,
          });
          setBrand(resolved);
          setCachedBrand(resolved, cacheKey);
          applyBrandToDocument(resolved);
          setIsLoading(false);
          return;
        }
      }

      // Resolve by program code
      if (programCode) {
        const response = await programUISettingsService.getPublicSettingsByCode(programCode);
        if (response.success && response.data) {
          const resolved = sanitizeBrandConfig({
            programId: null,
            programCode,
            name: response.data.tabTitle || response.data.programName || programCode,
            logo: response.data.loginPageLogoUrl || '',
            favicon: response.data.faviconUrl || '',
            sidebarLogo: response.data.internalSidebarLogoUrl || '',
            theme: {
              primaryColor: response.data.primaryColor || DEFAULT_BRAND.theme.primaryColor,
              secondaryColor: response.data.secondaryColor || DEFAULT_BRAND.theme.secondaryColor,
              accentColor: response.data.accentColor || DEFAULT_BRAND.theme.accentColor,
            },
            domain: tenant.value,
            loginBackground: {
              type: response.data.loginBackgroundType || 'gradient',
              value: response.data.loginBackgroundValue || '',
              patternEnabled: response.data.loginBackgroundPatternEnabled ?? true,
            },
            loginPageTitle: response.data.loginPageTitle || DEFAULT_BRAND.loginPageTitle,
            loginPageSubtitle: response.data.loginPageSubtitle || DEFAULT_BRAND.loginPageSubtitle,
            loginPageDescription: response.data.loginPageDescription || DEFAULT_BRAND.loginPageDescription,
          });
          setBrand(resolved);
          setCachedBrand(resolved, cacheKey);
          applyBrandToDocument(resolved);
        } else {
          // Fallback to default
          setBrand(DEFAULT_BRAND);
        }
      }
    } catch (err) {
      console.error('[WhiteLabel] Failed to resolve brand:', err);
      // Keep cached or default
    } finally {
      setIsLoading(false);
    }
  }, [tenant.type, tenant.value, cacheKey]);

  // Resolve on mount
  useEffect(() => {
    fetchBrand();
  }, [fetchBrand]);

  // Apply brand to document on change
  useEffect(() => {
    applyBrandToDocument(brand);
  }, [brand]);

  return (
    <WhiteLabelCtx.Provider
      value={{
        brand,
        isLoading,
        isWhiteLabeled: tenant.type !== 'none',
        tenantType: tenant.type,
        refreshBrand: fetchBrand,
      }}
    >
      {children}
    </WhiteLabelCtx.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useWhiteLabel() {
  const context = useContext(WhiteLabelCtx);
  if (context === undefined) {
    throw new Error('useWhiteLabel must be used within a WhiteLabelProvider');
  }
  return context;
}

// ─────────────────────────────────────────────────────────────────────────────
// Brand Logo Component
// ─────────────────────────────────────────────────────────────────────────────

export function BrandLogo({ className, variant = 'default' }: { className?: string; variant?: 'default' | 'sidebar' | 'login' }) {
  const { brand } = useWhiteLabel();

  const src = variant === 'sidebar' ? (brand.sidebarLogo || brand.logo) :
              variant === 'login' ? brand.logo :
              brand.logo;

  if (!src) return null;

  return (
    <img
      src={src}
      alt={brand.name}
      className={className || 'h-8 w-auto object-contain'}
    />
  );
}
